import {
  useCallback, useEffect, useRef, useState,
} from 'react';

import { DASHBOARD_MOTION } from './dashboardMotion';

type ChartEntranceGate = {
  setContainerRef: (node: unknown) => void;
  /** Chart may run entrance animation when true (25% visible at least once). */
  entranceUnlocked: boolean;
  /** Bump to remount chart layers after filter or axis changes. */
  replayGeneration: number;
};

/**
 * Defers below-fold chart entrance until 25% visible; replays on replayToken changes.
 */
export function useChartEntranceGate(replayToken: string | number): ChartEntranceGate {
  const [entranceUnlocked, setEntranceUnlocked] = useState(false);
  const [replayGeneration, setReplayGeneration] = useState(0);
  const hasUnlockedRef = useRef(false);
  const lastReplayToken = useRef(replayToken);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (lastReplayToken.current !== replayToken) {
      lastReplayToken.current = replayToken;
      if (hasUnlockedRef.current) {
        setReplayGeneration((g) => g + 1);
      }
    }
  }, [replayToken]);

  useEffect(() => () => {
    observerRef.current?.disconnect();
    observerRef.current = null;
  }, []);

  const unlock = useCallback(() => {
    if (hasUnlockedRef.current) return;
    hasUnlockedRef.current = true;
    setEntranceUnlocked(true);
    observerRef.current?.disconnect();
    observerRef.current = null;
  }, []);

  const setContainerRef = useCallback((node: unknown) => {
    // The test renderer host is not an Element, and `instanceof Element`
    // throws where that global is missing. Observe any node the ref receives.
    if (node == null || hasUnlockedRef.current) return;

    if (typeof IntersectionObserver === 'undefined') {
      unlock();
      return;
    }

    observerRef.current?.disconnect();
    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio >= DASHBOARD_MOTION.chartVisibleThreshold) {
          unlock();
        }
      },
      { threshold: [0, DASHBOARD_MOTION.chartVisibleThreshold, 1] },
    );
    observerRef.current.observe(node as Element);
  }, [unlock]);

  return {
    setContainerRef, entranceUnlocked, replayGeneration,
  };
}
